import { useState, useRef, useEffect } from "react";
import chatAgentService from "../services/RealChatAgentService";
import { updateSessionHash } from "./useSessionSync";
import { logger } from "../utils/logger";
import type { Message } from "../types/chat.types";

export interface UseSessionExpiryParams {
  /** Setter for messages */
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  /** Setter for isStreaming */
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>;
  /** Setter for isLoading */
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  /** Stable getToken reference (triggers replay re-check when auth arrives) */
  getToken: () => Promise<string | undefined>;
  /**
   * TRAC-475: where a replayed `human_handoff` goes. A reload can land after the reply text and
   * before the handoff event; the replay is then the only page that ever sees the event.
   */
  replayHandoffRef?: React.MutableRefObject<{
    onHandoff: (escalation?: string, department?: string) => void;
    onDone: () => void;
  } | null>;
}

export interface UseSessionExpiryReturn {
  // State
  sessionExpired: boolean;
  setSessionExpired: React.Dispatch<React.SetStateAction<boolean>>;
  needsReplay: boolean;
  setNeedsReplay: React.Dispatch<React.SetStateAction<boolean>>;
  replayStreamState: { startSequence?: number; isActive?: boolean } | null;
  setReplayStreamState: React.Dispatch<React.SetStateAction<{ startSequence?: number; isActive?: boolean } | null>>;
  // Refs
  replayAttemptedRef: React.MutableRefObject<boolean>;

  /**
   * TRACI-1106: the backend auto-cleared this shopper's session mid-turn (TRAC-402). Keep the
   * chat, mark the next message to carry it, and resend. No wipe, no "starting fresh" toast.
   */
  triggerExpiryResend: (resendFn: () => void) => void;
  /** Reset all session expiry state — called from handleClearSession */
  resetSessionExpiryState: () => void;
}

/**
 * TRAC-497: Session-expiry + interrupted-stream-replay concern extracted from
 * ChatInterface. Owns the expiry/toast/replay state and the TRAC-468 replay
 * effect; the streaming code in handleSendMessage calls the returned setters
 * and triggerExpiryResend. Behavior lifted verbatim from ChatInterface as of
 * the origin/main catch-up merge (incl. TRAC-518/631 trace fields on replay).
 */
export function useSessionExpiry(params: UseSessionExpiryParams): UseSessionExpiryReturn {
  const { setMessages, setIsStreaming, setIsLoading, getToken, replayHandoffRef } = params;

  // Session expiry state (TRAC-203: driven by S3 session sync, not timers)
  const [sessionExpired, setSessionExpired] = useState(false);

  // TRAC-468: Stream replay state (two-useEffect approach)
  const [needsReplay, setNeedsReplay] = useState(false);
  const [replayStreamState, setReplayStreamState] = useState<{ startSequence?: number; isActive?: boolean } | null>(null);
  const replayAttemptedRef = useRef(false);

  // TRACI-1106: the old "session started from expiry" flag hid the welcome on a wiped
  // chat; nothing wipes the chat now. Drop a flag left behind by an earlier version.
  useEffect(() => {
    try { localStorage.removeItem('traci_session_from_expiry'); } catch { /* storage unavailable */ }
  }, []);

  const triggerExpiryResend = (resendFn: () => void): void => {
    chatAgentService.carryConversationOnNextMessage();
    setIsLoading(false);
    setIsStreaming(false);
    setTimeout(resendFn, 100);
  };

  /** Reset all session expiry state — called from handleClearSession */
  const resetSessionExpiryState = (): void => {
    setSessionExpired(false);
  };

  // TRAC-468 useEffect 2: Replay interrupted stream once auth is ready.
  // In embed mode, partner token isn't set when useEffect 1 runs (child effects
  // fire before parent). This effect re-runs when getToken changes (which happens
  // after setPartnerToken triggers a re-render).
  useEffect(() => {
    if (!needsReplay || replayAttemptedRef.current) return;

    // Check auth
    const sessionId = chatAgentService.agentSessionId || chatAgentService.loadAgentSessionId();
    const hasAuth = !!chatAgentService.getPartnerToken();

    if (!sessionId || !hasAuth) {
      logger.debug(`[TRAC-468] Replay waiting — sessionId=${sessionId?.substring(0, 12) || 'null'} hasAuth=${hasAuth}`);
      // TRAC-850: don't wait forever. The mount effect already set isStreaming
      // (input locked) — if auth never becomes ready (e.g. a dead partner token
      // before TRAC-851's recovery kicks in), nothing else unlocks the input.
      // Give up after 20s and mark the turn interrupted; a successful auth
      // re-render before then clears this timer and replays normally.
      const giveUp = setTimeout(() => {
        logger.warn('[TRAC-850] Replay never became ready — marking turn interrupted');
        replayAttemptedRef.current = true;
        setNeedsReplay(false);
        setMessages(prev => {
          const updated = prev.map(msg =>
            msg.isStreaming ? { ...msg, isStreaming: false, wasInterrupted: true } : msg);
          chatAgentService.saveMessagesToSession(updated);
          return updated;
        });
        chatAgentService.updateStreamState(null, 0, false);
        setIsStreaming(false);
        setIsLoading(false);
      }, 20_000);
      return () => clearTimeout(giveUp);
    }

    replayAttemptedRef.current = true;

    // Find the streaming message (may not exist if refresh was before first chunk)
    const currentMessages = chatAgentService.getMessagesFromSession();
    const streamingMsg = currentMessages.find((m: Message) => m.isStreaming);
    const targetMsgId = streamingMsg?.id || `replay_${Date.now()}`;

    // If no streaming message, add a placeholder
    if (!streamingMsg) {
      setMessages(prev => [...prev, {
        id: targetMsgId, role: 'assistant' as const, content: '', timestamp: new Date(), isStreaming: true,
      }]);
    }

    const replayFrom = Math.max(0, (replayStreamState?.startSequence ?? 1) - 1);
    logger.debug(`[TRAC-468] Replaying from seq=${replayFrom}, session=${sessionId.substring(0, 12)}`);

    const replay = async () => {
      try {
        let accumulatedText = '';

        for await (const chunk of chatAgentService.streamResponse(sessionId, replayFrom)) {
          if (chunk.type === 'content' && chunk.content) {
            accumulatedText += chunk.content;
            const text = accumulatedText;
            setMessages(prev => prev.map(msg =>
              msg.id === targetMsgId
                ? { ...msg, parts: [...(msg.parts || []).filter(p => p.type !== 'text'), { type: 'text', content: text }], isStreaming: true }
                : msg
            ));
            setIsLoading(false);
          } else if (chunk.type === 'tool_use_start') {
            setIsLoading(false);
            setMessages(prev => prev.map(msg => {
              if (msg.id !== targetMsgId) return msg;
              const parts = [...(msg.parts || [])];
              parts.push({ type: 'tool', toolCall: { id: chunk.toolId || '', tool_name: chunk.toolName || '', status: 'running' as const, timestamp: new Date() } });
              return { ...msg, parts };
            }));
          } else if (chunk.type === 'tool_use_complete') {
            setMessages(prev => prev.map(msg => {
              if (msg.id !== targetMsgId) return msg;
              const parts = (msg.parts || []).map(p => {
                if (p.type === 'tool' && p.toolCall && (p.toolCall.id === chunk.toolId || p.toolCall.tool_name === chunk.toolName)) {
                  return { ...p, toolCall: { ...p.toolCall, status: 'completed' as const, result: chunk.result } };
                }
                return p;
              });
              return { ...msg, parts };
            }));
          } else if (chunk.type === 'ymmv_interface') {
            // Trigger YMMV vehicle search modal
            setMessages(prev => prev.map(msg => {
              if (msg.id !== targetMsgId) return msg;
              const parts = [...(msg.parts || [])];
              parts.push({ type: 'ymmv_interface', ...chunk });
              return { ...msg, parts };
            }));
          } else if (chunk.type === 'human_handoff') {
            // TRAC-475: dropped here before, so the replay restored "Connecting you to Sales now."
            // and nothing else: no queue, no Leave Queue, nobody asked.
            replayHandoffRef?.current?.onHandoff(chunk.escalation, chunk.department);
          } else if (chunk.type === 'done') {
            if (chunk.sessionHash) updateSessionHash(chunk.sessionHash);
            const doneTraceId = chunk.traceId;
            const doneLangfuseTraceUrl = chunk.langfuseTraceUrl;
            setMessages(prev => {
              const updated = prev.map(msg =>
                msg.id === targetMsgId
                  ? {
                      ...msg,
                      isStreaming: false,
                      ...(doneTraceId ? { traceId: doneTraceId } : {}),
                      ...(doneLangfuseTraceUrl ? { langfuseTraceUrl: doneLangfuseTraceUrl } : {}),
                    }
                  : msg
              );
              chatAgentService.saveMessagesToSession(updated);
              chatAgentService.updateStreamState(targetMsgId, 0, false);
              return updated;
            });
            setIsStreaming(false);
            setIsLoading(false);
            replayHandoffRef?.current?.onDone();
            break;
          }
        }

        if (accumulatedText === '') throw new Error('No content');
      } catch (error) {
        logger.error('[TRAC-468] Replay failed:', error);
        setMessages(prev => prev.map(msg =>
          msg.id === targetMsgId ? { ...msg, isStreaming: false, wasInterrupted: true } : msg
        ));
        chatAgentService.updateStreamState(null, 0, false);
        setIsStreaming(false);
        setIsLoading(false);
      }
    };

    const timeout = setTimeout(() => {
      logger.warn('[TRAC-468] Replay timed out');
      chatAgentService.closeActiveConnection();
      setMessages(prev => prev.map(msg =>
        msg.id === targetMsgId ? { ...msg, isStreaming: false, wasInterrupted: true } : msg
      ));
      chatAgentService.updateStreamState(null, 0, false);
      setIsStreaming(false);
      setIsLoading(false);
    }, 15000);

    replay().finally(() => { clearTimeout(timeout); setNeedsReplay(false); });

    return () => { clearTimeout(timeout); };
  }, [needsReplay, getToken, replayStreamState, setMessages, setIsStreaming, setIsLoading]);

  return {
    // State
    sessionExpired,
    setSessionExpired,
    needsReplay,
    setNeedsReplay,
    replayStreamState,
    setReplayStreamState,

    // Refs
    replayAttemptedRef,

    // Functions
    triggerExpiryResend,
    resetSessionExpiryState,
  };
}
